import { useEffect, type ReactNode } from 'react'
import type { FlashStatusType } from '@/Shared/AutoGenerated/Main'
import { AppLogo } from '@/React/Shared/Component/AppLogo'
import { FlashMessage } from '@/React/Shared/Component/Flash/FlashMessage'
import { flashMessageStore } from '@/React/Shared/Component/Flash/FlashMessageStore'

interface FreeToolLayoutProps {
    appName: string
    flashStatus: FlashStatusType | null
    children: ReactNode
    headerRight?: ReactNode
    footerLeft?: ReactNode
    footerRight?: ReactNode
    hasInternalScroll?: boolean
}

export function FreeToolLayout({
    appName,
    flashStatus,
    children,
    headerRight,
    footerLeft,
    footerRight,
    hasInternalScroll = true,
}: FreeToolLayoutProps) {
    // Renders flash messages coming from the server after a page reload/redirect
    useEffect(() => {
        if (flashStatus) {
            flashMessageStore.display(flashStatus)
        }
    }, [flashStatus])

    const hasFooterContent = Boolean(footerLeft) || Boolean(footerRight)

    let mainBorderClassName = 'border-t border-stone-200'

    if (hasFooterContent) {
        mainBorderClassName = 'border-y border-stone-200'
    }

    const containerClassName = hasInternalScroll
        ? 'bg-white flex flex-col min-h-svh max-h-svh'
        : 'bg-white flex flex-col min-h-svh'

    const mainClassName = hasInternalScroll
        ? `flex-1 overflow-y-auto bg-gray-50 ${mainBorderClassName}`
        : `flex-1 bg-gray-50 ${mainBorderClassName}`

    return (
        <div className={containerClassName}>
            <nav>
                <div className="max-w-6xl mx-auto py-4 px-4 lg:px-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="shrink-0">
                                <a href="/">
                                    <AppLogo appName={appName} />
                                </a>
                            </div>
                        </div>
                        <div>{headerRight}</div>
                    </div>
                </div>
            </nav>

            <main className={mainClassName}>
                <div className="max-w-5xl mx-auto py-4 px-4 lg:px-0">
                    {children}
                </div>
            </main>

            {hasFooterContent && (
                <footer>
                    <div className="max-w-6xl mx-auto py-4 px-4 lg:px-0">
                        <div className="flex justify-between items-center">
                            <div>{footerLeft}</div>
                            <div>{footerRight}</div>
                        </div>
                    </div>
                </footer>
            )}

            <FlashMessage />
        </div>
    )
}
